import { useState } from 'react'
import { useApp } from '@/context/AppContext'
import AppHeader from '@/components/layout/AppHeader'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import { data as api } from '@/utils/auth'

// ── HIPAA consent modal ───────────────────────────────────────────────────────
function HipaaModal({ onAgree, onDecline }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-sm lg:max-w-md w-full p-6 flex flex-col gap-4 shadow-xl"
        role="dialog" aria-modal="true" aria-label="HIPAA notice">
        <div className="flex items-center gap-3">
          <span className="text-3xl">🔒</span>
          <div>
            <p className="font-bold text-slate-800">HIPAA Notice</p>
            <p className="text-xs text-slate-500">Medical Records Section</p>
          </div>
        </div>
        <div className="text-sm text-slate-600 leading-relaxed space-y-2">
          <p>This section stores protected health information (PHI) about your child. By accessing it you agree that:</p>
          <ul className="list-disc list-inside space-y-1 pl-1">
            <li>Every access is logged with a timestamp and user identity.</li>
            <li>Records are encrypted at rest on Cloudflare's infrastructure (AES-256).</li>
            <li>Sharing records externally is your responsibility — obtain proper consent.</li>
            <li>GoodGuardian is not a covered entity under HIPAA and does not replace formal medical record systems.</li>
          </ul>
        </div>
        <div className="disclaimer">
          ⚠️ For emergency access, call 911 or your child's provider directly.
        </div>
        <div className="flex gap-3">
          <Button variant="ghost" size="md" className="flex-1" onClick={onDecline}>Cancel</Button>
          <Button variant="primary" size="md" className="flex-1" onClick={onAgree}>I Understand</Button>
        </div>
      </div>
    </div>
  )
}

// ── Child form ────────────────────────────────────────────────────────────────
const EMPTY_CHILD = {
  name: '', dob: '', school_name: '', school_address: '', school_phone: '',
  teacher_name: '', grade: '', allergies: '', medications: '', blood_type: '',
  insurance_provider: '', insurance_id: '',
  pediatrician_name: '', pediatrician_phone: '', pediatrician_address: '', notes: '',
}

function ChildForm({ initial = EMPTY_CHILD, onSave, onCancel, loading, initialTab = 'basic' }) {
  const [form, setForm] = useState(initial)
  const [tab, setTab]   = useState(initialTab) // basic | school | health
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  return (
    <div className="flex flex-col h-full">
      <div className="flex border-b border-slate-100 flex-shrink-0" role="tablist" aria-label="Child profile sections">
        {[['basic','👤 Basic'],['school','🎓 School'],['health','🏥 Health']].map(([id, lbl]) => (
          <button key={id} onClick={() => setTab(id)} role="tab" aria-selected={tab === id}
            className={`flex-1 py-3 text-xs font-semibold transition-colors ${tab === id ? 'text-brand-700 border-b-2 border-brand-700' : 'text-slate-500'}`}>
            {lbl}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3">
        {tab === 'basic' && (
          <>
            <Input label="Child's name" value={form.name} onChange={e => set('name', e.target.value)} required />
            <Input label="Date of birth" type="date" value={form.dob} onChange={e => set('dob', e.target.value)} />
            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1">Notes</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
                rows={3} className="input-base resize-none text-sm"
                placeholder="Any other important information..." />
            </div>
          </>
        )}
        {tab === 'school' && (
          <>
            <Input label="School name" value={form.school_name} onChange={e => set('school_name', e.target.value)} />
            <Input label="Grade" value={form.grade} onChange={e => set('grade', e.target.value)} placeholder="e.g. 3rd grade" />
            <Input label="Teacher name" value={form.teacher_name} onChange={e => set('teacher_name', e.target.value)} />
            <Input label="School phone" type="tel" value={form.school_phone} onChange={e => set('school_phone', e.target.value)} />
            <Input label="School address" value={form.school_address} onChange={e => set('school_address', e.target.value)} />
          </>
        )}
        {tab === 'health' && (
          <>
            <Input label="Pediatrician name" value={form.pediatrician_name} onChange={e => set('pediatrician_name', e.target.value)} />
            <Input label="Pediatrician phone" type="tel" value={form.pediatrician_phone} onChange={e => set('pediatrician_phone', e.target.value)} />
            <Input label="Allergies" value={form.allergies} onChange={e => set('allergies', e.target.value)} placeholder="e.g. Peanuts, Penicillin" />
            <Input label="Current medications" value={form.medications} onChange={e => set('medications', e.target.value)} />
            <Input label="Blood type" value={form.blood_type} onChange={e => set('blood_type', e.target.value)} placeholder="e.g. O+" />
            <Input label="Insurance provider" value={form.insurance_provider} onChange={e => set('insurance_provider', e.target.value)} />
            <Input label="Insurance ID" value={form.insurance_id} onChange={e => set('insurance_id', e.target.value)} />
          </>
        )}
      </div>
      <div className="px-4 py-3 flex gap-3 border-t border-slate-100 flex-shrink-0">
        <Button variant="ghost" size="md" className="flex-1" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" size="md" className="flex-1" loading={loading}
          disabled={!form.name} onClick={() => onSave(form)}>
          Save
        </Button>
      </div>
    </div>
  )
}

// ── Medical record types ──────────────────────────────────────────────────────
const MED_TYPES = {
  visit:       { label: '🩺 Doctor Visit',     badge: 'bg-blue-100 text-blue-700'   },
  vaccination: { label: '💉 Vaccination',       badge: 'bg-green-100 text-green-700' },
  prescription:{ label: '💊 Prescription',      badge: 'bg-purple-100 text-purple-700'},
  lab:         { label: '🧪 Lab / Test Result', badge: 'bg-yellow-100 text-yellow-700'},
  insurance:   { label: '📋 Insurance',          badge: 'bg-slate-100 text-slate-600'  },
  other:       { label: '📌 Other',              badge: 'bg-slate-100 text-slate-600'  },
}

// ── Add medical record modal ──────────────────────────────────────────────────
function AddMedModal({ childId, onClose, onSave }) {
  const [form, setForm] = useState({ child_id: childId, record_type: 'visit', date: '', provider_name: '', provider_phone: '', diagnosis: '', treatment: '', notes: '' })
  const [loading, setLoading] = useState(false)
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  const handleSave = async () => {
    if (!form.record_type) return
    setLoading(true)
    await onSave(form)
    setLoading(false)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end lg:items-center lg:justify-center z-50" onClick={onClose}>
      <div className="w-full max-w-lg mx-auto bg-white rounded-t-3xl lg:rounded-3xl p-6 flex flex-col gap-4 animate-slide-up max-h-[90vh] overflow-y-auto"
        role="dialog" aria-modal="true" aria-label="Add medical record"
        onClick={e => e.stopPropagation()}>
        <div className="w-12 h-1 rounded-full bg-slate-200 mx-auto -mt-2 mb-1" />
        <h3 className="font-bold text-slate-800">Add Medical Record</h3>

        <div>
          <label className="text-sm font-semibold text-slate-700 block mb-2">Record type</label>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(MED_TYPES).map(([val, cfg]) => (
              <button key={val} onClick={() => set('record_type', val)}
                className={`text-xs px-2 py-2 rounded-xl font-medium border-2 text-left transition-all ${
                  form.record_type === val ? cfg.badge + ' border-current' : 'bg-white text-slate-700 border-slate-300'}`}>
                {cfg.label}
              </button>
            ))}
          </div>
        </div>

        <Input label="Date" type="date" value={form.date} onChange={e => set('date', e.target.value)} />
        <Input label="Provider name" value={form.provider_name} onChange={e => set('provider_name', e.target.value)} placeholder="Dr. Smith or clinic name" />
        <Input label="Provider phone" type="tel" value={form.provider_phone} onChange={e => set('provider_phone', e.target.value)} />
        <Input label="Diagnosis / Vaccine administered" value={form.diagnosis} onChange={e => set('diagnosis', e.target.value)} />
        <Input label="Treatment / Prescription" value={form.treatment} onChange={e => set('treatment', e.target.value)} />
        <div>
          <label className="text-sm font-semibold text-slate-700 block mb-1">Notes</label>
          <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
            rows={3} className="input-base resize-none text-sm" placeholder="Additional notes..." />
        </div>

        <div className="flex gap-3">
          <Button variant="ghost" size="md" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="md" className="flex-1" loading={loading} onClick={handleSave}>
            Save Record
          </Button>
        </div>
      </div>
    </div>
  )
}

// ── Children screen ───────────────────────────────────────────────────────────
export default function Children() {
  const { children, addChild, updateChild, deleteChild } = useApp()
  const [selectedId, setSelectedId]   = useState(null)
  const [childTab, setChildTab]       = useState('profile') // profile | medical | school
  const [showAddChild, setShowAddChild] = useState(false)
  const [showEditChild, setShowEditChild] = useState(false)
  const [editInitialTab, setEditInitialTab] = useState('basic')
  const [childLoading, setChildLoading] = useState(false)
  const [hipaaAgreed, setHipaaAgreed]   = useState(false)
  const [showHipaa, setShowHipaa]       = useState(false)
  const [medRecords, setMedRecords]     = useState([])
  const [medLoading, setMedLoading]     = useState(false)
  const [showAddMed, setShowAddMed]     = useState(false)

  const selectedChild = children.find(c => c.id === selectedId)

  const loadMedRecords = async (childId) => {
    setMedLoading(true)
    try {
      const res = await api.getMedical(childId)
      setMedRecords(res.records || [])
    } catch { /* non-fatal */ }
    setMedLoading(false)
  }

  const handleSelectChild = (id) => {
    setSelectedId(id)
    setChildTab('profile')
    setMedRecords([])
  }

  const handleTabChange = (tab) => {
    setChildTab(tab)
    if (tab === 'medical' && !hipaaAgreed) {
      setShowHipaa(true)
      return
    }
    if (tab === 'medical' && hipaaAgreed && selectedId) {
      loadMedRecords(selectedId)
    }
  }

  const handleHipaaAgree = () => {
    setHipaaAgreed(true)
    setShowHipaa(false)
    setChildTab('medical')
    if (selectedId) loadMedRecords(selectedId)
  }

  const handleAddChild = async (form) => {
    setChildLoading(true)
    await addChild(form)
    setChildLoading(false)
    setShowAddChild(false)
  }

  const handleUpdateChild = async (form) => {
    setChildLoading(true)
    await updateChild(selectedId, form)
    setChildLoading(false)
    setShowEditChild(false)
  }

  const handleAddMedRecord = async (form) => {
    await api.postMedRecord(form)
    if (selectedId) loadMedRecords(selectedId)
  }

  const handleDeleteChild = async (id) => {
    await deleteChild(id)
    setSelectedId(null)
  }

  const calcAge = (dob) => {
    if (!dob) return null
    const diff = Date.now() - new Date(dob).getTime()
    const age  = Math.floor(diff / (365.25 * 24 * 3600 * 1000))
    return `${age} yrs`
  }

  // ── Edit child screen ─────────────────────────────────────────────────────
  if (showEditChild && selectedChild) {
    return (
      <div className="flex flex-col h-full bg-white lg:bg-slate-50">
        <AppHeader title="Edit Child Profile" showBack onBack={() => setShowEditChild(false)} />
        <div className="flex-1 lg:px-6 lg:py-6">
          <div className="h-full lg:max-w-4xl lg:mx-auto lg:bg-white lg:rounded-2xl lg:border lg:border-slate-200 lg:overflow-hidden">
            <ChildForm
              initial={selectedChild}
              initialTab={editInitialTab}
              onSave={handleUpdateChild}
              onCancel={() => setShowEditChild(false)}
              loading={childLoading}
            />
          </div>
        </div>
      </div>
    )
  }

  // ── Add child screen ──────────────────────────────────────────────────────
  if (showAddChild) {
    return (
      <div className="flex flex-col h-full bg-white lg:bg-slate-50">
        <AppHeader title="Add Child Profile" showBack onBack={() => setShowAddChild(false)} />
        <div className="flex-1 lg:px-6 lg:py-6">
          <div className="h-full lg:max-w-4xl lg:mx-auto lg:bg-white lg:rounded-2xl lg:border lg:border-slate-200 lg:overflow-hidden">
            <ChildForm
              onSave={handleAddChild}
              onCancel={() => setShowAddChild(false)}
              loading={childLoading}
            />
          </div>
        </div>
      </div>
    )
  }

  // ── Child detail screen ───────────────────────────────────────────────────
  if (selectedChild) {
    return (
      <div className="flex flex-col h-full bg-white lg:bg-slate-50">
        <AppHeader title={selectedChild.name} showBack onBack={() => setSelectedId(null)}
          right={
            <div className="flex items-center gap-1">
              <button onClick={() => { setEditInitialTab('basic'); setShowEditChild(true) }}
                className="tap-target text-brand-700 hover:text-brand-800 transition-colors"
                title="Edit child profile"
                aria-label="Edit child profile">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button onClick={() => handleDeleteChild(selectedChild.id)}
                className="tap-target text-red-400 hover:text-red-600 transition-colors"
                title="Remove child"
                aria-label="Remove child profile">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          }
        />

        {/* Child avatar header */}
        <div className="brand-gradient px-5 py-4 flex items-center gap-4 flex-shrink-0">
          <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-2xl font-bold text-white">
            {selectedChild.name[0].toUpperCase()}
          </div>
          <div>
            <p className="text-white font-bold text-base">{selectedChild.name}</p>
            {selectedChild.dob && <p className="text-blue-100 text-sm">{calcAge(selectedChild.dob)} · DOB: {selectedChild.dob}</p>}
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex border-b border-slate-100 flex-shrink-0" role="tablist" aria-label="Child detail sections">
          {[['profile','👤 Profile'],['school','🎓 School'],['medical','🏥 Medical']].map(([id, lbl]) => (
            <button key={id} onClick={() => handleTabChange(id)} role="tab" aria-selected={childTab === id}
              className={`flex-1 py-3 text-xs font-semibold transition-colors ${childTab === id ? 'text-brand-700 border-b-2 border-brand-700' : 'text-slate-500'}`}>
              {lbl}
            </button>
          ))}
        </div>

        <div className="screen no-scrollbar px-4 sm:px-6 lg:px-8 py-4">
          <div className="mx-auto w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-4">

          {/* Profile tab */}
          {childTab === 'profile' && (
            <>
              <div className="card flex flex-col gap-2 lg:col-span-2">
                <p className="text-sm font-bold text-slate-500 uppercase tracking-wide">Basic Info</p>
                <p className="text-base font-semibold text-slate-800">{selectedChild.name}</p>
                {selectedChild.dob && <p className="text-sm text-slate-500">Date of Birth: {selectedChild.dob}</p>}
              </div>
              {selectedChild.notes && (
                <InfoRow icon="📝" label="Notes" value={selectedChild.notes} />
              )}
              {!selectedChild.dob && !selectedChild.notes && (
                <div className="text-center py-6 text-slate-500 lg:col-span-2">
                  <p className="text-sm">No additional profile info yet.</p>
                  <button className="text-xs text-brand-700 font-semibold mt-2 underline"
                    onClick={() => { setEditInitialTab('basic'); setShowEditChild(true) }}>
                    Tap here to edit profile
                  </button>
                </div>
              )}
            </>
          )}

          {/* School tab */}
          {childTab === 'school' && (
            <>
              {selectedChild.school_name ? (
                <>
                  <div className="card flex flex-col gap-2">
                    <p className="text-sm font-bold text-slate-500 uppercase tracking-wide">School</p>
                    <p className="text-base font-semibold text-slate-800">{selectedChild.school_name}</p>
                    {selectedChild.grade && <p className="text-sm text-slate-500">Grade: {selectedChild.grade}</p>}
                    {selectedChild.teacher_name && <p className="text-sm text-slate-500">Teacher: {selectedChild.teacher_name}</p>}
                    {selectedChild.school_phone && <p className="text-sm text-brand-700">📞 {selectedChild.school_phone}</p>}
                    {selectedChild.school_address && <p className="text-sm text-slate-500">📍 {selectedChild.school_address}</p>}
                  </div>
                </>
              ) : (
                <div className="text-center py-6 text-slate-500">
                  <p className="text-sm">No school info yet.</p>
                  <Button variant="secondary" size="sm" className="mt-3"
                    onClick={() => { setEditInitialTab('school'); setShowEditChild(true) }}>
                    + Add School Info
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Medical tab */}
          {childTab === 'medical' && hipaaAgreed && (
            <>
              <div className="flex items-center justify-between lg:col-span-2">
                <p className="text-sm font-bold text-slate-500 uppercase tracking-wide">Medical Records</p>
                <button onClick={() => setShowAddMed(true)}
                  className="text-sm font-semibold text-brand-700 hover:text-brand-800"
                  aria-label="Add medical record">
                  + Add Record
                </button>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-700 flex gap-2 lg:col-span-2">
                <span>🔒</span>
                <span>Access to this section is logged for HIPAA compliance. All records are encrypted at rest.</span>
              </div>
              {medLoading ? (
                <div className="text-center py-8 text-slate-500 text-sm lg:col-span-2">Loading records…</div>
              ) : medRecords.length === 0 ? (
                <div className="text-center py-8 text-slate-500 lg:col-span-2">
                  <p className="text-sm">No medical records yet</p>
                  <Button variant="secondary" size="sm" className="mt-3" onClick={() => setShowAddMed(true)}>Add First Record</Button>
                </div>
              ) : (
                medRecords.map(rec => {
                  const cfg = MED_TYPES[rec.record_type] || MED_TYPES.other
                  return (
                    <div key={rec.id} className="card flex flex-col gap-1.5">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-bold px-2 py-0.5 rounded-full ${cfg.badge}`}>{cfg.label}</span>
                        {rec.date && <span className="text-sm text-slate-500">{rec.date}</span>}
                      </div>
                      {rec.provider_name && <p className="text-base font-semibold text-slate-800">{rec.provider_name}</p>}
                      {rec.diagnosis    && <InfoRow icon="🩺" label="Diagnosis"   value={rec.diagnosis}    compact />}
                      {rec.treatment    && <InfoRow icon="💊" label="Treatment"    value={rec.treatment}    compact />}
                      {rec.notes        && <InfoRow icon="📝" label="Notes"        value={rec.notes}        compact />}
                    </div>
                  )
                })
              )}
              {showAddMed && (
                <AddMedModal childId={selectedChild.id} onClose={() => setShowAddMed(false)} onSave={handleAddMedRecord} />
              )}
            </>
          )}
          </div>
        </div>

        {showHipaa && <HipaaModal onAgree={handleHipaaAgree} onDecline={() => setShowHipaa(false)} />}
      </div>
    )
  }

  // ── Children list ─────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full bg-white lg:bg-slate-50">
      <AppHeader title="Child Profiles"
        right={
          <button onClick={() => setShowAddChild(true)}
            className="tap-target w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white/80"
            aria-label="Add child profile">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </button>
        }
      />

      <div className="screen no-scrollbar px-4 sm:px-6 lg:px-8 py-4">
        <div className="mx-auto w-full max-w-6xl flex flex-col gap-4">
        {children.length === 0 ? (
          <div className="card bg-brand-50 border border-brand-200 text-center py-10 flex flex-col items-center gap-3">
            <span className="text-4xl">👶</span>
            <p className="font-bold text-slate-800">Add your first child profile</p>
            <p className="text-xs text-slate-500 leading-relaxed">
              Store school contacts, medical info, allergies, and insurance details in one secure place — shared with your co-parent.
            </p>
            <Button variant="primary" size="md" onClick={() => setShowAddChild(true)}>Add Child Profile</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {children.map(child => (
              <button key={child.id} onClick={() => handleSelectChild(child.id)}
                className="card flex items-center gap-4 text-left hover:shadow-card-hover active:scale-[0.98] transition-all">
                <div className="w-12 h-12 rounded-full brand-gradient flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                  {child.name[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-800">{child.name}</p>
                  <p className="text-sm text-slate-500 mt-0.5">
                    {child.dob ? `${calcAge(child.dob)} · ` : ''}
                    {child.school_name || 'No school info'}
                  </p>
                  {child.allergies && (
                    <p className="text-sm text-red-600 mt-0.5 font-medium">⚠️ Allergies on file</p>
                  )}
                </div>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4 text-slate-300 flex-shrink-0">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ))}
          </div>
        )}
        </div>
      </div>
    </div>
  )
}

function calcAge(dob) {
  if (!dob) return null
  const diff = Date.now() - new Date(dob).getTime()
  return `${Math.floor(diff / (365.25 * 24 * 3600 * 1000))} yrs`
}

function InfoRow({ icon, label, value, compact }) {
  return (
    <div className={compact ? 'flex gap-1 items-start' : 'card flex gap-3 items-start'}>
      {!compact && <span className="text-base flex-shrink-0">{icon}</span>}
      <div className="flex-1 min-w-0">
        {!compact && <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-0.5">{label}</p>}
        <p className="text-sm text-slate-700 leading-relaxed">
          {compact && <span className="text-slate-500">{label}: </span>}{value}
        </p>
      </div>
    </div>
  )
}
